//@flow
import * as Immutable from 'immutable';

import {
    Geometry,
    LatLngLineSegments,
    CompositeLatLngPolygon,
    LatLngPolygon,
    LatLngLineSegment,
    LatLngPolygonPoints
} from 'common/models/flow/Geometry';
import type {
    GeometryModelType,
    LatLngLineSegmentsModelType,
    CompositeLatLngPolygonModelType,
    LatLngPolygonModelType,
    LatLngLineSegmentModelType,
    LatLngPolygonPointsModelType
} from 'common/models/flow/Geometry';

import { LatLng } from './LatLng';

class InternalLatLngPolygonPoints extends LatLngPolygonPoints {
    static fromJS(
        json: LatLngPolygonPointsModelType
    ): InternalLatLngPolygonPoints {
        const state: Object = Object.assign({}, json);
        state.points = Immutable.List(state.points).map(item =>
            LatLng.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get points(): Immutable.List<LatLng> {
        return this._state.get('points');
    }
}

export { InternalLatLngPolygonPoints as LatLngPolygonPoints };
export const ProviderEnumType = {
    CLIENT_READ: 'CLIENT_READ',
    CLIENT_GEOCODER: 'CLIENT_GEOCODER',
    LOCUS_GEOCODER: 'LOCUS_GEOCODER'
};

class InternalGeometry extends Geometry {
    static fromJS(json: GeometryModelType): InternalGeometry {
        const state: Object = Object.assign({}, json);
        state.latLng = state.latLng
            ? LatLng.fromJS(state.latLng)
            : state.latLng;
        return new this(Immutable.Map(state));
    }

    get latLng(): ?LatLng {
        return this._state.get('latLng');
    }

    getProviderIcon(): string {
        if (this.provider === ProviderEnumType.LOCUS_GEOCODER) {
            return 'system';
        }
        return 'avatar';
    }

    static getDefault(): InternalGeometry {
        return this.fromJS({
            latLng: {
                lat: null,
                lng: null,
                accuracy: null
            }
        });
    }
}

class InternalLatLngLineSegments extends LatLngLineSegments {
    static fromJS(
        json: LatLngLineSegmentsModelType
    ): InternalLatLngLineSegments {
        const state: Object = Object.assign({}, json);
        state.segments = Immutable.List(state.segments).map(item =>
            InternalLatLngLineSegment.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get segments(): Immutable.List<LatLngLineSegment> {
        return this._state.get('segments');
    }
}

class InternalCompositeLatLngPolygon extends CompositeLatLngPolygon {
    static fromJS(
        json: CompositeLatLngPolygonModelType
    ): InternalCompositeLatLngPolygon {
        const state: Object = Object.assign({}, json);
        state.polygons = Immutable.List(state.polygons).map(item =>
            InternalLatLngPolygon.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get polygons(): Immutable.List<LatLngPolygon> {
        return this._state.get('polygons');
    }
}

class InternalLatLngPolygon extends LatLngPolygon {
    static fromJS(json: LatLngPolygonModelType): InternalLatLngPolygon {
        const state: Object = Object.assign({}, json);
        state.vertices = Immutable.List(state.vertices).map(item =>
            LatLng.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get vertices(): Immutable.List<LatLng> {
        return this._state.get('vertices');
    }
}

class InternalLatLngLineSegment extends LatLngLineSegment {
    static fromJS(json: LatLngLineSegmentModelType): InternalLatLngLineSegment {
        const state: Object = Object.assign({}, json);
        state.latLng1 = LatLng.fromJS(state.latLng1);
        state.latLng2 = LatLng.fromJS(state.latLng2);
        return new this(Immutable.Map(state));
    }

    get latLng1(): LatLng {
        return this._state.get('latLng1');
    }

    get latLng2(): LatLng {
        return this._state.get('latLng2');
    }
}

export {
    InternalGeometry as Geometry,
    InternalLatLngLineSegments as LatLngLineSegments,
    InternalCompositeLatLngPolygon as CompositeLatLngPolygon,
    InternalLatLngPolygon as LatLngPolygon,
    InternalLatLngLineSegment as LatLngLineSegment
};
